/**
 * Listing registry (BAL-664, layer 1 of docs/architecture/listing-surfaces.md).
 *
 * `defineListing` is the SINGLE source of truth for a public directory's facets,
 * sorts, and search columns. Its two consumers derive everything else:
 *   - `applyListingFilters(query, registry, state)` → the DB predicates + order
 *   - `toFilterConfig(registry, t)`                 → the ListingFilterConfig UI
 *
 * The registry is a DB-facing artifact: it holds i18n KEYS (not translated
 * strings) and column names, never JSX or embeds. Embeds / DTO mapping / media
 * resolution / pagination stay surface-owned in each loader.
 */

/** How a facet's selected values map to a PostgREST predicate. */
export type FacetPredicate =
  /** `.eq(column, value[0])` — single mutually-exclusive value. */
  | 'eq'
  /** `.in(column, values)` — multi-select over a scalar column. */
  | 'in'
  /** `.contains(column, values)` — array column contains ALL values. */
  | 'contains'
  /**
   * `.or('col.cs.{"v"},…,col.is.null')` — array/text column containing any of
   * the values OR null. Values MUST be in the facet `allowList` (injection-safe).
   */
  | 'orIncludingNull';

/** A registry facet. `column` is a real column on the entity's Row. */
export interface ListingFacet<Row> {
  key: string;
  kind: 'toggle' | 'dropdown' | 'range';
  column: keyof Row & string;
  predicate: FacetPredicate;
  multi?: boolean;
  /**
   * Static options (value + i18n key). Omit for dynamic sets (cities, brands) —
   * those are injected at `toFilterConfig` time via `dynamicOptions`.
   */
  options?: readonly { value: string; labelKey: string }[];
  /** Required for the `orIncludingNull` predicate — the injection allow-list. */
  allowList?: readonly string[];
  /** Range facet bounds (kind === 'range'). */
  min?: number;
  max?: number;
  unit?: string;
  /** When true, this facet also powers a crawlable SEO route segment (layer 6). */
  seoSegment?: boolean;
}

/** One column of an order-by. The last term of every sort must be `id`. */
export interface OrderTerm {
  column: string;
  ascending: boolean;
  /** Default false (NULLS LAST) — the safe default for nullable sort keys. */
  nullsFirst?: boolean;
}

/** A sort option. `orderBy` is consumed by applyListingFilters, never shipped to the UI. */
export interface ListingSort {
  value: string;
  labelKey: string;
  orderBy: OrderTerm[];
}

/** A PostgREST-ish query the surface has already started with `.from().select()`. */
export interface FilterableQuery {
  eq(column: string, value: unknown): FilterableQuery;
  in(column: string, values: readonly unknown[]): FilterableQuery;
  contains(column: string, value: unknown): FilterableQuery;
  or(filter: string): FilterableQuery;
  ilike(column: string, pattern: string): FilterableQuery;
  order(
    column: string,
    options: { ascending: boolean; nullsFirst?: boolean },
  ): FilterableQuery;
}

export interface ListingRegistry<Row> {
  table: string;
  search?: { columns: readonly (keyof Row & string)[] };
  facets: readonly ListingFacet<Row>[];
  sorts: readonly ListingSort[];
  defaultSort: string;
}

/**
 * Build + validate a listing registry. Enforces the total-order guardrail: every
 * sort's order-by MUST end with the `id` column, or offset/keyset pagination
 * reshuffles rows that tie on a nullable/non-unique key (see BAL-663's 804
 * score-NULL orgs). Throws at module-load time if a sort violates it.
 */
export function defineListing<Row>(
  registry: ListingRegistry<Row>,
): ListingRegistry<Row> {
  for (const sort of registry.sorts) {
    const last = sort.orderBy[sort.orderBy.length - 1];
    if (!last || last.column !== 'id') {
      throw new Error(
        `defineListing(${registry.table}): sort "${sort.value}" must end its ` +
          `orderBy with the "id" total-order tiebreak (got ` +
          `"${last?.column ?? '<empty>'}"). Add { column: 'id', ascending: true }.`,
      );
    }
  }
  if (!registry.sorts.some((s) => s.value === registry.defaultSort)) {
    throw new Error(
      `defineListing(${registry.table}): defaultSort "${registry.defaultSort}" ` +
        `is not one of the defined sorts.`,
    );
  }
  return registry;
}
